import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from "../Breadcrumb";
import Modal from "../Modal/Modal";
import { logoutUser } from "../../actions/user";
import { validateLoginId, validateName, validateEmail } from "../../helpers/validation";
import "./AdminEdit.css"

const AdminEdit = React.memo(({
    isFetching,
    detail,
    fetchAdminDetail,
    fetchUserInfo,
    user_info,
    deleteAdmin,
    updateAdmin     
}) => {

    const { adminId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [modalConfirmAction, setModalConfirmAction] = useState(() => () => {});

    const [form, setForm] = useState({
        login_id: "",
        admin_name: "",
        mail_address: "",
        otp_enable: false,
    });

    const [errors, setErrors] = useState({
        login_id: "",
        admin_name: "",
        mail_address: ""
    });

    // 初期ロード時にユーザー情報と管理者詳細を取得
    useEffect(() => {
        fetchUserInfo();
        fetchAdminDetail(adminId);
    // eslint-disable-next-line
    }, [adminId]);

    // 詳細データ取得後、フォームに反映
    useEffect(() => {
        if (detail) {
            setForm({
                login_id: detail.login_id || "",
                admin_name: detail.admin_name || "",
                mail_address: detail.mail_address || "",
                otp_enable: detail.otp_enable === 1,
            });
        }
    }, [detail]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm({ ...form, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleCheckbox = () => {
        const newOtp = !form.otp_enable;

        setForm({ ...form, otp_enable: newOtp });

        if (!newOtp && errors.mail_address) {
            setErrors({ ...errors, mail_address: "" });
        }
    };

    // 更新処理
    const handleUpdate = async () => {
        // Validate
        const newErrors = {};

        const loginError = validateLoginId(form.login_id);
        if (loginError) newErrors.login_id = loginError;

        const nameError = validateName(form.admin_name);
        if (nameError) newErrors.admin_name = nameError;

        const emailError = form.otp_enable ? validateEmail(form.mail_address) : "";
        if (emailError) newErrors.mail_address = emailError;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Clear errors
        setErrors({
            login_id: "",
            admin_name: "",
            mail_address: ""
        });

        const payload = {
            admin_id: adminId,
            login_id: form.login_id,
            admin_name: form.admin_name,
            otp_enable: form.otp_enable ? 1 : 0,
            mail_address: form.mail_address || ""
        };

        const result = await updateAdmin(payload);

        if (result?.payload) {
            navigate("/admin-list");
            return;
        }

        setModalMessage(result?.error || "更新に失敗しました。");
        setModalConfirmAction(() => () => setIsModalOpen(false));
        setIsModalOpen(true);
    };

    // 削除処理
    const executeDelete = async () => {
        
        if (String(user_info?.admin_id) === String(adminId)) {
            setModalMessage("自分自身を削除することはできません。");
            setModalConfirmAction(() => () => setIsModalOpen(false));
            setIsModalOpen(true);
            return;
        }

        setIsModalOpen(false);

        const result = await deleteAdmin(adminId);

        if (result?.payload) {
            navigate("/admin-list");
            return;
        }

        setModalMessage(result?.error || "削除に失敗しました。");
        setModalConfirmAction(() => () => setIsModalOpen(false));
        setIsModalOpen(true);
    };

    return (
        <div id="Main">
            <div id="Contents">

                <Breadcrumb 
                    user={user_info?.admin_name}
                    onLogout={() => dispatch(logoutUser())} 
                />

                {/* モーダル */}
                {isModalOpen && (
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        message={modalMessage}
                        onConfirm={modalConfirmAction}
                        showCancelButton={modalMessage === "本当に削除しますか？"}
                    />
                )}

                <div className="admin-layout">
                    <div className="admin-card">
                        <h2 className="admin-title">管理者編集</h2>

                        <div className="admin-edit-btn-row">
                            <div className="btn-row-left">
                                <button 
                                    className="btn-edit-password" 
                                    onClick={() => navigate(`/admin-list/edit/${adminId}/password-edit`)}
                                >
                                    パスワード編集
                                </button>
                            </div>

                            <div className="btn-row-right">
                                <button className="btn-update" onClick={handleUpdate}>更新</button>
                                <button
                                    className="btn-delete"
                                    onClick={() => {
                                        setModalMessage("本当に削除しますか？");
                                        setModalConfirmAction(() => executeDelete);
                                        setIsModalOpen(true);
                                    }}
                                >
                                    削除
                                </button>
                                <button className="btn-back" onClick={() => navigate("/admin-list")}>
                                    戻る
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="admin-edit-form-box">
                            {/* LOGIN ID */}
                            <div className="form-row">
                                <label className="admin-edit-label">ログインID</label>
                                <input
                                    name="login_id"
                                    value={form.login_id}
                                    onChange={handleChange}
                                    className="admin-edit-input"
                                />
                            </div>
                            {errors.login_id && <p className="error-text">{errors.login_id}</p>}

                            {/* ADMIN NAME */}
                            <div className="form-row">
                                <label className="admin-edit-label">ユーザー名</label>
                                <input
                                    name="admin_name"
                                    value={form.admin_name}
                                    onChange={handleChange}
                                    className="admin-edit-input"
                                />
                            </div>
                            {errors.admin_name && <p className="error-text">{errors.admin_name}</p>}

                            {/* OTP */}
                            <div className="form-row">
                                <label className="admin-edit-label">二段階認証</label>
                                <div className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={form.otp_enable}
                                        onChange={handleCheckbox}
                                    />
                                    <span className="admin-edit-checkbox-text">
                                        二段階認証を使用する
                                    </span>
                                </div>
                            </div>

                            {/* EMAIL ADDRESS */}
                            <div className={`mail-animate ${form.otp_enable ? "show" : ""}`}>
                                <div className="form-row">
                                    <label className="admin-edit-label">メールアドレス</label>
                                    <input
                                        name="mail_address"
                                        value={form.mail_address}
                                        onChange={handleChange}
                                        className="admin-edit-input"
                                    />
                                </div>
                                {errors.mail_address && (
                                    <p className="error-text">{errors.mail_address}</p>
                                )}
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            <div className={`overlay ${isFetching ? 'active' : ''}`}>
                <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
            </div>
        </div>
    );
});

AdminEdit.displayName = "AdminEdit";    // fix eslint(react/display-name)

AdminEdit.propTypes = {
    isFetching: PropTypes.bool.isRequired,
    detail: PropTypes.object,
    error: PropTypes.any,
    fetchAdminDetail: PropTypes.func.isRequired,
    fetchUserInfo: PropTypes.func.isRequired,
    deleteAdmin: PropTypes.func.isRequired,
    updateAdmin: PropTypes.func.isRequired,
    user_info: PropTypes.object,
};

export default AdminEdit;